/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Link, Typography2 as Typography } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import CourseEnrollButton from 'bundles/enroll-common/components/CourseEnrollButton';
import RatingLine from 'bundles/reviews-common/components/cds/RatingLine';

import _t from 'i18n!nls/reviews';

const DESCRIPTION_TRUNCATE_AT = 600;

export type ReviewsHeaderRatings = {
  averageFiveStarRating: number;
  ratingCount: number;
  commentCount: number;
};

type Props = {
  courseName: string;
  courseId: string;
  programSlug?: string;
  coursePageHref: string;
  ratings?: ReviewsHeaderRatings | null;
  description?: string | null;
  showEnrollButton?: boolean;
};

const styles = {
  header: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-200);
  `,
  title: css`
    font-weight: 600;
  `,
  enrollButton: css`
    margin-bottom: var(--cds-spacing-200);
    width: 30%;
    height: 36px;
  `,
  ratingDate: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  dotSeparator: css`
    margin: 0 var(--cds-spacing-100);
  `,
  aboutText: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  aboutContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    align-items: flex-start;
  `,
};

const ReviewsHeader = ({ courseName, courseId, programSlug, coursePageHref, ratings, description }: Props) => {
  const [showFullDescription, setShowFullDescription] = React.useState(false);
  const isLong = (description?.length ?? 0) > DESCRIPTION_TRUNCATE_AT;
  const showTruncated = isLong && !showFullDescription;
  const text = showTruncated ? `${description?.slice(0, DESCRIPTION_TRUNCATE_AT)}…` : description ?? '';

  return (
    <div data-testid="reviews-header" css={styles.header}>
      <Typography variant="subtitleLarge" component="h2" css={styles.title}>
        {_t('Learner reviews & feedback for ')}
        <Link href={coursePageHref} variant="standardSecondary">
          {courseName}
        </Link>
      </Typography>
      <RatingLine rating={ratings?.averageFiveStarRating ?? 0} numReviews={ratings?.ratingCount ?? 0} />
      {!programSlug && (
        <CourseEnrollButton
          courseId={courseId}
          showStartDate={false}
          enrollButtonText={_t('Enroll for free')}
          renderEnrollButton={({ onClick, disabled, enrollButtonText: text, href, ariaLabel }) =>
            href ? (
              <Button
                component="a"
                href={href}
                variant="primary"
                css={styles.enrollButton}
                icon={<ArrowNextIcon />}
                aria-label={ariaLabel}
              >
                {text}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={onClick}
                disabled={disabled}
                css={styles.enrollButton}
                icon={<ArrowNextIcon />}
                aria-label={ariaLabel}
              >
                {text}
              </Button>
            )
          }
        />
      )}

      {description && (
        <div css={styles.aboutContainer} aria-live="polite">
          <Typography variant="subtitleMedium" component="h3">
            {_t('About this course')}
          </Typography>
          <Typography variant="bodySecondary" component="p" css={styles.aboutText}>
            {text}
          </Typography>
          {isLong && (
            <Link
              component="button"
              variant="quiet"
              onClick={() => setShowFullDescription((v) => !v)}
              aria-expanded={showFullDescription}
            >
              {showFullDescription ? _t('Show less') : _t('Show more')}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewsHeader;
